import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { NotificationService } from '@shared/services/notification.service';
import { ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { Subject, takeUntil } from 'rxjs';
import { CreateBatchSubscription } from '../../graphql/subscriptions/on-create-batch';
import { DeleteBatchSubscription } from '../../graphql/subscriptions/on-delete-batch';
import { UpdateBatchSubscription } from '../../graphql/subscriptions/on-update-batch';
import { CreateBatchComponent } from './create-batch/create-batch.component';
import { DeleteBatchComponent } from './delete-batch/delete-batch.component';
import { EditBatchComponent } from './edit-batch/edit-batch.component';
import { BatchRow, groupBatchRows, GroupedBatchRow, isReorderBatch } from './mes-batch-grouping';
import { IBatch, integerToBatchStatus, isInteger, MesBatchService } from './mes-batch-service';

interface IError {
	alias: string;
	name: string;
	type: string;
	message: string;
}

type ITableViewModelItem = BatchRow & {
	alias: string;
	name: string;
	sourceId?: string;
	status: string;
	batch: IBatch;
	errors: IError[];
};

@Component({
	selector: 'app-mes-batch',
	templateUrl: './mes-batch.component.html',
	styleUrls: ['./mes-batch.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MesBatchComponent implements OnDestroy, AfterViewInit {
	private destroy$: Subject<void> = new Subject<void>();
	private batches: IBatch[] = [];
	private filterValue = '';
	private currentSort: Sort = { active: 'alias', direction: 'asc' };
	terminals: Map<string, ITerminal>;
	displayedColumns: string[] = ['name', 'status', 'menu'];
	dataSource = new MatTableDataSource<GroupedBatchRow>();
	showSearch = false;
	isLoading = false;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.batch.dialog.create-batch-btn',
			click: () => this.openCreate(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private service: MesBatchService,
		private terminalService: TerminalAdministrationService,
		private onBatchUpdate: UpdateBatchSubscription,
		private onBatchDelete: DeleteBatchSubscription,
		private onBatchCreate: CreateBatchSubscription,
	) {
		this.dataSource.sortData = (data) => data;
	}

	ngOnInit() {
		this.terminalService
			.getTerminals()
			.pipe(takeUntil(this.destroy$))
			.subscribe((terminals) => {
				this.terminals = new Map(terminals.result.map((terminal) => [terminal.id, terminal]));
				this.updateList();
			});

		this.onBatchUpdate
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe((e) => {
				this.updateBatch(e.onUpdateBatch);
			});

		this.onBatchCreate
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe((e) => {
				this.updateBatch(e.onCreateBatch);
			});

		this.onBatchDelete
			.subscribe$()
			.pipe(takeUntil(this.destroy$))
			.subscribe((e) => {
				this.deleteBatch(e.onDeleteBatch.id);
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	public onSort(sort: Sort) {
		this.currentSort = sort?.direction ? { active: sort.active, direction: sort.direction } : { active: 'alias', direction: 'asc' };
		this.rebuildTableData();
	}

	private getTableViewModel(batches: IBatch[]): ITableViewModelItem[] {
		return (
			batches?.map((batch) => {
				const status = isInteger(batch.status) ? integerToBatchStatus(batch.status) : batch.status;
				const viewBatch: IBatch = {
					...batch,
					status,
				};
				return {
					alias: viewBatch.alias,
					batch: viewBatch,
					name: viewBatch.name,
					sourceId: viewBatch.sourceId,
					status: viewBatch.status,
					errors:
						batch.errors?.map((error) => ({
							alias: this.terminals.get(error.terminalId)?.alias || '',
							name: this.terminals.get(error.terminalId)?.name || '',
							type: error.type,
							message: error.message,
						})) ?? [],
				};
			}) ?? []
		);
	}

	updateBatch(batch: IBatch) {
		if (!batch || !this.terminals) {
			return;
		}

		const idx = this.batches.findIndex((x) => x.id === batch.id);

		if (idx > -1) {
			this.batches[idx] = batch;
		} else {
			this.batches.push(batch);
		}
		this.rebuildTableData();
	}

	deleteBatch(id: string): void {
		if (!id || !this.batches?.length) {
			return;
		}

		this.batches = this.batches.filter((x) => x.id !== id);
		this.rebuildTableData();
	}

	updateList() {
		if (this.isLoading || !this.terminals) {
			return;
		}

		this.isLoading = true;
		this.service.getAll().subscribe((c) => {
			this.batches = c.result ?? [];
			this.rebuildTableData();
			this.dataSource.sort = this.sort;
			this.isLoading = false;
		});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			this.filterValue = event.target.value.trim().toLowerCase();
			this.rebuildTableData();
			this.paginator?.firstPage();
		}
	}

	openCreate() {
		this.dialog.open(CreateBatchComponent, {
			disableClose: true,
			width: '650px',
			data: null,
		});
	}

	openEdit(item: IBatch | null) {
		if (!item || isReorderBatch(item)) {
			return;
		}

		this.dialog.open(EditBatchComponent, {
			disableClose: true,
			width: '650px',
			data: item,
		});
	}

	openDelete(item: IBatch) {
		if (isReorderBatch(item)) {
			return;
		}

		const dialogRef = this.dialog.open(DeleteBatchComponent, {
			disableClose: true,
			width: '450px',
			data: item,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.notificationService.notify(['mes.administration.batch.delete.notification.header'], ['mes.administration.batch.delete.notification.content', { alias: item.alias }], 'success');
		});
	}

	private rebuildTableData() {
		const tableViewModel = this.getTableViewModel(this.batches);
		this.dataSource.data = groupBatchRows(tableViewModel, this.currentSort, this.filterValue);
	}
}
